import { Link } from "@tanstack/react-router";
import { ArrowRight, Check, Sparkles } from "lucide-react";
import { CLUSTERS, type ClusterKey } from "@/lib/clusters";
import { getProductHeroImage } from "@/lib/productHeroImages";

/**
 * Blog → Product authority flow.
 *
 * Every article ends with this systematic panel so link equity from the
 * article flows to (a) the cluster's own PDP as the primary recommendation
 * and (b) the two other TruWe PDPs as the "full range". Search engines see
 * consistent, keyword-anchored internal links from every spoke to every
 * product hub — the pattern that builds topical authority.
 *
 * Rendered as <aside role="complementary"> so crawlers treat it as
 * supplementary navigation attached to the article body.
 */
export function AuthorityFlow({ cluster }: { cluster: ClusterKey }) {
  const primary = CLUSTERS[cluster];
  const others = (Object.keys(CLUSTERS) as ClusterKey[])
    .filter((k) => k !== cluster)
    .map((k) => CLUSTERS[k]);

  return (
    <aside
      aria-label={`Shop the science behind ${primary.productTitle}`}
      className="mt-14 overflow-hidden rounded-3xl border border-brand/15 bg-gradient-to-br from-brand/5 via-background to-brand-deep/5"
    >
      {/* Primary product, the cluster hub */}
      <div className="p-6 md:p-10">
        <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-brand">
          <Sparkles className="h-3.5 w-3.5" />
          Shop the science
        </div>
        <div className="mt-3 flex items-start gap-4">
          {getProductHeroImage(primary.productHandle, primary.productTitle) && (
            <img
              src={getProductHeroImage(primary.productHandle, primary.productTitle)!}
              alt={`${primary.productTitle} supplement bottle`}
              width={96}
              height={96}
              loading="lazy"
              className="h-20 w-20 shrink-0 rounded-2xl border border-border bg-card object-contain p-1.5 md:h-24 md:w-24"
            />
          )}
          <div>
            <h2 className="font-display text-2xl font-bold text-brand-deep md:text-3xl">
              {primary.productTitle}
            </h2>
            <p className="mt-2 text-base text-foreground/80 md:text-lg">
              {primary.productTagline}
            </p>
          </div>
        </div>

        <ul className="mt-5 grid gap-2 sm:grid-cols-2">
          {primary.productBenefits.map((b) => (
            <li key={b} className="flex items-start gap-2 text-sm text-foreground/85">
              <Check className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
              <span>{b}</span>
            </li>
          ))}
        </ul>
        <Link
          to="/products/$handle"
          params={{ handle: primary.productHandle }}
          className="mt-6 inline-flex items-center gap-2 rounded-full bg-brand px-6 py-3 text-sm font-semibold text-white shadow-luxury transition hover:bg-brand-deep"
        >
          Shop {primary.productTitle} <ArrowRight className="h-4 w-4" />
        </Link>
      </div>

      {/* Full range, passes equity to the other two PDPs from every article */}
      <div className="border-t border-brand/10 bg-background/60 p-6 md:p-8">
        <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
          The full TruWe range
        </p>
        <ul className="mt-4 grid gap-3 sm:grid-cols-2">
          {others.map((c) => (
            <li key={c.key}>
              <Link
                to="/products/$handle"
                params={{ handle: c.productHandle }}
                className="group flex h-full items-start justify-between gap-3 rounded-2xl border border-border bg-card p-4 transition hover:-translate-y-0.5 hover:border-brand/40 hover:shadow-md"
              >
                {getProductHeroImage(c.productHandle, c.productTitle) && (
                  <img
                    src={getProductHeroImage(c.productHandle, c.productTitle)!}
                    alt={`${c.productTitle} supplement bottle`}
                    width={56}
                    height={56}
                    loading="lazy"
                    className="h-12 w-12 shrink-0 rounded-xl border border-border bg-background object-contain p-1"
                  />
                )}
                <span className="min-w-0">
                  <span className="block font-display text-sm font-semibold text-brand-deep group-hover:text-brand">
                    {c.productTitle}
                  </span>
                  <span className="mt-1 block text-xs text-muted-foreground">
                    {c.productTagline}
                  </span>
                </span>

                <ArrowRight className="mt-1 h-4 w-4 shrink-0 text-brand transition group-hover:translate-x-0.5" />
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </aside>
  );
}
